import { getCookie, getQueryParam } from '@vfde-sails/utils';
import { getCMSConfig, getTariffIds } from '../../../util/cms';
import { AppState } from '../interface';
import { STORAGE_KEY } from '../../../constants';
import { debugInfo } from '../../../util/debug';
import { isNotEmptyString, isValidId } from '../../../util/validator';
import { initialState } from '../reducer';
import { State } from '../index';
import { QueryNames, Cookies } from '../../../enums/network';
import { OverlayContent } from '../../../enums/elementId';
import { getSessionStorageItemJson, setSessionStorageItemJson } from '@vfde-sails/storage';
import { getAddressFromCookie } from '../../../util/cookieHelper';
import { SessionAppState } from '../interface';
import { ProductType } from '../../../enums/product';

const getStateFromPageOptions = (draftState: AppState) => {
    const config = getCMSConfig();

    if (!config) {
        return draftState;
    }

    const { selected, activeProviderType } = config;

    if (selected && activeProviderType) {
        draftState.selected.tariffId = selected.tariffId;
        draftState.selected.tariff = undefined;
        draftState.activeProviderType = activeProviderType;
    }

    return draftState;
};

export const saveStateToSession = (state: AppState) => {
    const { gigakombi, familyAndFriends, selected } = state;

    const sessionAppState: SessionAppState = {
        [ProductType.FIBRE]: {
            gigakombi: gigakombi,
            familyAndFriends: familyAndFriends,
            selected: selected,
        },
    };

    setSessionStorageItemJson(STORAGE_KEY, sessionAppState);
};

const getStateFromSession = (draftState: AppState) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);
    const appState: Partial<AppState> | undefined = sessionState?.fibre;

    if (!appState) {
        debugInfo('Failed to load state from SessionStorage');
        return draftState;
    }

    return { ...draftState, ...appState };
};

const getStateFromQuery = (draftState: AppState) => {
    const tariffId = getQueryParam(QueryNames.TARIFF);
    const gigakombi = getQueryParam(QueryNames.GIGAKOMBI);

    if (isNotEmptyString(gigakombi)) {
        draftState.gigakombi = true;
    }

    if (isValidId(tariffId, getTariffIds(draftState.activeProviderType))) {
        draftState.selected.tariffId = tariffId as string;
        draftState.selected.tariff = draftState.tariffs[draftState.activeProviderType]
            .find(tariff => tariff.id === tariffId);
    }

    return draftState;
};

const getStateFromCookie = (draftState: AppState) => {
    draftState.familyAndFriends = Boolean(getCookie(Cookies.FF));

    try {
        draftState.address = getAddressFromCookie();
    } catch (error) {
        draftState.address = undefined;
        draftState.avcResult = undefined;
    }

    return draftState;
};

/**
 * Load state from sessionStorage, additionalPageOptions, query params, cookies.
 *
 * @returns AppState
 */
export const loadInitialAppState = (): Partial<AppState> => {
    // we need a deep copy of initialState because 'immer' makes it read-only
    let draftState: AppState = JSON.parse(JSON.stringify(initialState));

    draftState = getStateFromPageOptions(draftState);
    draftState = getStateFromSession(draftState);
    draftState = getStateFromQuery(draftState);
    draftState = getStateFromCookie(draftState);

    return draftState;
};

/* istanbul ignore next */
export const getUpdateFlags = (state: State, oldState: State) => {
    const {
        loading,
        activeOverlay,
        overlayLoaded,
        avcResult,
        selected,
        selectedTariff,
        isOverlayOpen,
        tariffs,
        userInfo,
        preMarketingType,
    } = state;
    const avcResultChanged = oldState.avcResult !== avcResult;
    const tariffChanged = selected.tariffId && selected.tariffId !== oldState.selected.tariffId;
    const overlayOpened = isOverlayOpen && oldState.isOverlayOpen !== isOverlayOpen;
    const tariffsFirstLoad = !oldState.tariffs?.length && !!tariffs?.length;
    const userInfoChanged = oldState.userInfo !== userInfo;

    return {
        loading: loading !== oldState.loading,
        overlay: overlayLoaded && oldState.overlayLoaded !== overlayLoaded,
        footnote: (avcResultChanged || tariffChanged || tariffsFirstLoad) && !Boolean(preMarketingType),
        avcOpened: selectedTariff && overlayOpened && activeOverlay === OverlayContent.FORM,
        overlayChanged: activeOverlay !== oldState.activeOverlay,
        tariffChanged,
        avcResultChanged,
        userInfoChanged,
    };
};
